import { useRef, useState, type ChangeEvent } from "react";
import { apiClient, ApiError } from "../api/client.js";

// 3.A.4-laajennus: polku-picker-modaali source-rootien lisäykseen ja konvention
// asetukseen.
//
// HUOM tärkeää: web-selain EI saa absoluuttista polkua filesystemistä
// turvallisuussyistä. `<input type="file" webkitdirectory>` palauttaa vain
// suhteellisen polun (esim. "Pictures/clip.mov" kun käyttäjä valitsi
// /Users/kimmos/Pictures). Käytämme tätä SILTI: kansio-picker avautuu, käyttäjä
// vahvistaa kansion → täytämme inputin "$HOME/<kansionnimi>" -ehdotuksella ja
// käyttäjä korvaa "$HOME" oikealla polulla.
//
// Oikea absoluuttinen-polku-browser tulee vaiheen 4 macOS-natiivi-toolkitissa
// jossa Finder-extension tarjoaa "Liitä Krattiin" -context-menun.

type Shortcut = {
  label: string;
  path: string;
  description?: string;
};

const SHORTCUTS: Shortcut[] = [
  { label: "~/Pictures", path: "$HOME/Pictures", description: "Macin oletuskuva-kansio" },
  { label: "~/Movies", path: "$HOME/Movies", description: "Macin oletusvideo-kansio" },
  { label: "~/Documents", path: "$HOME/Documents", description: "Asiakirjat" },
  { label: "~/Desktop", path: "$HOME/Desktop", description: "Työpöytä" },
  { label: "~/Downloads", path: "$HOME/Downloads", description: "Lataukset" },
  { label: "~/ProjectVault", path: "$HOME/ProjectVault", description: "Krattin oletus-projektikansio" },
  { label: "/Volumes", path: "/Volumes", description: "USB- ja Thunderbolt-levyt" },
];

export type PathPickerModalProps = {
  title?: string;
  /** Initialisoitu input-arvo (kun avataan jo aloitetulta inputilta). */
  initialPath?: string;
  /** Daemonin machineId — jos asetettu, kayttaja saa "Selaa Finderissa" -napin. */
  machineId?: string;
  onSelect: (path: string) => void;
  onClose: () => void;
};

type FinderPickStatus = "idle" | "requesting" | "waiting" | "completed" | "cancelled" | "error";

export function PathPickerModal({
  title = "Valitse polku",
  initialPath = "",
  machineId,
  onSelect,
  onClose,
}: PathPickerModalProps) {
  const [draft, setDraft] = useState(initialPath);
  const [folderHint, setFolderHint] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [finderStatus, setFinderStatus] = useState<FinderPickStatus>("idle");
  const [finderError, setFinderError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  // 3.A.7.4 — Selaa Finderissa (osascript daemonin kautta)
  async function openFinderPicker() {
    if (!machineId) return;
    setFinderStatus("requesting");
    setFinderError(null);
    try {
      const created = await apiClient.postInboxRequest(machineId, {
        type: "pick-folder",
        request: { prompt: title },
      });
      setFinderStatus("waiting");
      pollInboxResult(created.id);
    } catch (err) {
      setFinderStatus("error");
      setFinderError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err)
      );
    }
  }

  function pollInboxResult(requestId: string) {
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 1000;
    const TIMEOUT_MS = 90_000;

    const tick = async () => {
      if (Date.now() - startedAt > TIMEOUT_MS) {
        setFinderStatus("error");
        setFinderError(
          "Ei vastausta — onko daemon käynnissä? Tarkista /levyt-näkymästä."
        );
        return;
      }
      try {
        const s = await apiClient.getInboxStatus(requestId);
        if (s.status === "completed") {
          const path = (s.result as { path?: string } | null)?.path;
          if (typeof path === "string") {
            setDraft(path);
            setFinderStatus("completed");
            setFolderHint(null);
            return;
          }
          setFinderStatus("error");
          setFinderError("Daemon palautti odottamattoman tuloksen.");
          return;
        }
        if (s.status === "cancelled") {
          setFinderStatus("cancelled");
          return;
        }
        if (s.status === "failed" || s.status === "timeout") {
          setFinderStatus("error");
          setFinderError(s.error ?? "Daemon-puolen virhe.");
          return;
        }
        // pending tai in_progress → jatka pollaamista
        pollingRef.current = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
      } catch (err) {
        setFinderStatus("error");
        setFinderError(err instanceof Error ? err.message : String(err));
      }
    };
    void tick();
  }

  function cancelPolling() {
    if (pollingRef.current !== null) {
      window.clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    setFinderStatus("idle");
  }

  function applyShortcut(s: Shortcut) {
    setDraft(s.path);
    setFolderHint(null);
  }

  function openFolderPicker() {
    fileInputRef.current?.click();
  }

  function handleFolderSelected(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    // webkitRelativePath = "<rootName>/<subpath>" — selain antaa vain
    // valitun kansion nimen + alikansiot. Absoluuttinen polku ei tule.
    const firstFile = files[0]!;
    const relPath = (firstFile as File & { webkitRelativePath: string }).webkitRelativePath ?? "";
    const rootName = relPath.split("/")[0] ?? firstFile.name;
    setFolderHint(rootName);
    setDraft(`$HOME/${rootName}`);
    // Tyhjennetään input, jotta saman kansion uudelleenvalinta laukaisee change-eventin
    e.target.value = "";
  }

  const canSelect =
    draft.trim().startsWith("/") &&
    !draft.includes("/../") &&
    !draft.includes("$HOME");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-slate-700 bg-slate-900 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-100">{title}</h3>

        {/* Pää-CTA: Selaa Finderissa (osascript daemonin kautta) */}
        {machineId && (
          <div className="mt-4 rounded-lg border border-cyan-700/40 bg-cyan-950/20 p-4">
            <h4 className="text-sm font-semibold text-cyan-200">
              🍎 Selaa Finderissa Macilla
            </h4>
            <p className="mt-1 text-xs text-slate-400">
              Avaa Macin natiivi Open-dialogin daemonin kautta. Vastaus 2–5 s.
            </p>
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={openFinderPicker}
                disabled={finderStatus === "requesting" || finderStatus === "waiting"}
                className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
              >
                {finderStatus === "requesting" || finderStatus === "waiting"
                  ? "Avataan Finder Macilla…"
                  : "📁 Selaa Finderissa"}
              </button>
              {(finderStatus === "requesting" || finderStatus === "waiting") && (
                <button
                  type="button"
                  onClick={cancelPolling}
                  className="rounded bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-600"
                >
                  Lopeta odotus
                </button>
              )}
            </div>
            {finderStatus === "completed" && (
              <p className="mt-2 text-xs text-green-300">
                ✓ Polku haettu Finderista.
              </p>
            )}
            {finderStatus === "cancelled" && (
              <p className="mt-2 text-xs text-yellow-300">
                Käyttäjä peruutti Finder-dialogissa.
              </p>
            )}
            {finderStatus === "error" && (
              <p className="mt-2 text-xs text-red-400">
                Virhe: {finderError ?? "tuntematon"}
              </p>
            )}
          </div>
        )}

        <p className="mt-4 text-xs text-slate-500">
          Tai käytä pikakuvakkeita / käsisyöttöä alla:
        </p>

        <div className="mt-4">
          <label className="block text-xs uppercase tracking-wide text-slate-400">
            Pikakuvakkeet
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {SHORTCUTS.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => applyShortcut(s)}
                title={s.description}
                className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs uppercase tracking-wide text-slate-400">
            Tai selaa kansio
          </label>
          <button
            type="button"
            onClick={openFolderPicker}
            className="mt-1 rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            📁 Avaa kansio-picker
          </button>
          <input
            ref={fileInputRef}
            type="file"
            // @ts-expect-error -- webkitdirectory ei ole standardi-attribuutti, mutta kaikki selaimet tukevat
            webkitdirectory=""
            directory=""
            multiple
            onChange={handleFolderSelected}
            className="hidden"
          />
          {folderHint && (
            <p className="mt-2 text-xs text-yellow-300">
              Valitsit kansion <code className="rounded bg-slate-800 px-1">{folderHint}</code>.
              Selain ei paljasta sen täyttä polkua — täydennä alle oikea
              absoluuttinen polku (esim.
              <code className="mx-1 rounded bg-slate-800 px-1">/Users/kimmos/{folderHint}</code>).
            </p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-xs uppercase tracking-wide text-slate-400">
            Polku
          </label>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="/Users/<sinun-nimi>/Pictures"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSelect) {
                onSelect(draft.trim());
                onClose();
              }
            }}
            className="mt-1 w-full rounded bg-slate-800 px-3 py-2 font-mono text-sm text-slate-100 placeholder-slate-500"
          />
          {draft.includes("$HOME") && (
            <p className="mt-1 text-xs text-yellow-400">
              Korvaa <code>$HOME</code> Macin home-polulla (esim.{" "}
              <code>/Users/kimmos</code>) — selain ei tunne käyttäjänimeäsi.
            </p>
          )}
          {!canSelect && draft.length > 0 && !draft.includes("$HOME") && (
            <p className="mt-1 text-xs text-red-400">
              Polun pitää alkaa /:llä eikä saa sisältää &quot;..&quot;-segmenttiä.
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-700 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={() => {
              onSelect(draft.trim());
              onClose();
            }}
            disabled={!canSelect}
            className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
          >
            Valitse
          </button>
        </div>
      </div>
    </div>
  );
}
